import axiosInstance from "../../api/axiosConfig";
import { handleAPIError, handleBinaryAPIError } from "../../api/utils/errors";
import type {
  CasePrepUscisFilledForm,
  CasePrepUscisFormCatalogEntry,
  CasePrepUscisFormVersion,
  CasePrepUscisSource,
  CasePrepUscisSourcesResponse
} from "./types";

const individualFormsUrl = (individualId: string) =>
  `/individuals/${individualId}/case_extend_uscis_forms`;

export const CasePrepUscisFormsAPI = {
  async listCatalog(): Promise<CasePrepUscisFormCatalogEntry[]> {
    try {
      const response = await axiosInstance.get<{
        forms: CasePrepUscisFormCatalogEntry[];
      }>("/case_extend_uscis_forms/catalog");
      return Array.isArray(response.data?.forms) ? response.data.forms : [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async listFilledForms(
    individualId: string,
    caseId: string
  ): Promise<CasePrepUscisFilledForm[]> {
    try {
      const response = await axiosInstance.get<{
        filled_forms: CasePrepUscisFilledForm[];
      }>(individualFormsUrl(individualId), { params: { case_id: caseId } });
      return Array.isArray(response.data?.filled_forms)
        ? response.data.filled_forms
        : [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async getFilledForm(
    individualId: string,
    filledFormId: string
  ): Promise<CasePrepUscisFilledForm> {
    try {
      const response = await axiosInstance.get<{
        filled_form: CasePrepUscisFilledForm;
      }>(`${individualFormsUrl(individualId)}/${filledFormId}`);
      return response.data.filled_form;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async listSources(
    individualId: string,
    params: { case_id: string; form_number: string }
  ): Promise<CasePrepUscisSourcesResponse> {
    try {
      const response = await axiosInstance.get<CasePrepUscisSourcesResponse>(
        `${individualFormsUrl(individualId)}/sources`,
        { params }
      );
      return {
        evidence_documents: Array.isArray(response.data?.evidence_documents)
          ? response.data.evidence_documents
          : [],
        case_files: Array.isArray(response.data?.case_files)
          ? response.data.case_files
          : [],
        intakes: Array.isArray(response.data?.intakes)
          ? response.data.intakes
          : []
      };
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async recommendSources(
    individualId: string,
    payload: { case_id: string; form_number: string }
  ): Promise<CasePrepUscisSource[]> {
    try {
      const response = await axiosInstance.post<{
        recommendations: CasePrepUscisSource[];
      }>(`${individualFormsUrl(individualId)}/sources/recommend`, payload);
      return Array.isArray(response.data?.recommendations)
        ? response.data.recommendations
        : [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async createAndRun(
    individualId: string,
    payload: {
      case_id: string;
      form_number: string;
      source_steps: CasePrepUscisSource[];
      run_immediately?: boolean;
      flatten_pdf?: boolean;
      initialize_blank?: boolean;
    }
  ): Promise<CasePrepUscisFilledForm> {
    try {
      const response = await axiosInstance.post<{
        filled_form: CasePrepUscisFilledForm;
      }>(individualFormsUrl(individualId), payload);
      return response.data.filled_form;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async sendIntake(
    individualId: string,
    payload: {
      case_id: string;
      form_number: string;
      recipient_email?: string;
      expires_in_days?: number;
      attorney_note?: string;
    }
  ): Promise<void> {
    try {
      await axiosInstance.post(
        `${individualFormsUrl(individualId)}/intakes/send`,
        payload
      );
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async deleteFilledForm(
    individualId: string,
    filledFormId: string
  ): Promise<void> {
    try {
      await axiosInstance.delete(
        `${individualFormsUrl(individualId)}/${filledFormId}`
      );
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async getRunStepPdf(
    individualId: string,
    filledFormId: string,
    stepId: string
  ): Promise<Blob> {
    try {
      const response = await axiosInstance.get<Blob>(
        `${individualFormsUrl(
          individualId
        )}/${filledFormId}/run_steps/${stepId}/pdf`,
        { responseType: "blob" }
      );
      return response.data;
    } catch (error) {
      const apiError = await handleBinaryAPIError(error);
      throw apiError;
    }
  },

  async getFilledFormPdf(
    individualId: string,
    filledFormId: string
  ): Promise<Blob> {
    try {
      const response = await axiosInstance.get<Blob>(
        `${individualFormsUrl(individualId)}/${filledFormId}/pdf`,
        { params: { _: Date.now() }, responseType: "blob" }
      );
      return response.data;
    } catch (error) {
      const apiError = await handleBinaryAPIError(error);
      throw apiError;
    }
  },

  async generateFilledForm(
    individualId: string,
    filledFormId: string,
    payload: {
      field_values?: Record<string, unknown>;
      flatten_pdf?: boolean;
      initialize_blank?: boolean;
    }
  ): Promise<CasePrepUscisFilledForm> {
    try {
      const response = await axiosInstance.post<{
        filled_form: CasePrepUscisFilledForm;
      }>(
        `${individualFormsUrl(individualId)}/${filledFormId}/generate`,
        payload
      );
      return response.data.filled_form;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /**
   * A manual save: the changed values plus the PDF the form screen's editor
   * filled. The server stores the PDF as is; no Extend run.
   */
  async saveFilledForm(
    individualId: string,
    filledFormId: string,
    payload: { field_values: Record<string, unknown>; pdf: ArrayBuffer }
  ): Promise<CasePrepUscisFilledForm> {
    const form = new FormData();
    form.append("field_values", JSON.stringify(payload.field_values));
    form.append(
      "pdf",
      new Blob([payload.pdf], { type: "application/pdf" }),
      "form.pdf"
    );
    try {
      const response = await axiosInstance.post<{
        filled_form: CasePrepUscisFilledForm;
      }>(`${individualFormsUrl(individualId)}/${filledFormId}/save`, form, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      return response.data.filled_form;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async runFilledForm(
    individualId: string,
    filledFormId: string,
    payload: {
      source_steps: CasePrepUscisSource[];
      flatten_pdf?: boolean;
      overwrite_existing_values?: boolean;
    }
  ): Promise<CasePrepUscisFilledForm> {
    try {
      const response = await axiosInstance.post<{
        filled_form: CasePrepUscisFilledForm;
      }>(`${individualFormsUrl(individualId)}/${filledFormId}/run`, payload);
      return response.data.filled_form;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async listFilledFormVersions(
    individualId: string,
    filledFormId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<CasePrepUscisFormVersion[]> {
    try {
      const response = await axiosInstance.get<{
        versions: CasePrepUscisFormVersion[];
      }>(`${individualFormsUrl(individualId)}/${filledFormId}/versions`, {
        params
      });
      return Array.isArray(response.data?.versions)
        ? response.data.versions
        : [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async restoreFilledFormVersion(
    individualId: string,
    filledFormId: string,
    versionId: string
  ): Promise<CasePrepUscisFilledForm> {
    try {
      const response = await axiosInstance.post<{
        filled_form: CasePrepUscisFilledForm;
      }>(
        `${individualFormsUrl(
          individualId
        )}/${filledFormId}/versions/${versionId}/restore`
      );
      return response.data.filled_form;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async getFilledFormVersionPdf(
    individualId: string,
    filledFormId: string,
    versionId: string
  ): Promise<Blob> {
    try {
      const response = await axiosInstance.get<Blob>(
        `${individualFormsUrl(
          individualId
        )}/${filledFormId}/versions/${versionId}/pdf`,
        // Cache-bust: the URL is stable per version, so a browser-cached copy
        // could otherwise shadow a freshly re-rendered one.
        { responseType: "blob", params: { v: Date.now() } }
      );
      return response.data;
    } catch (error) {
      const apiError = await handleBinaryAPIError(error);
      throw apiError;
    }
  },

  async getFieldCitationPdf(
    individualId: string,
    filledFormId: string,
    sourceDocumentId: string
  ): Promise<Blob> {
    try {
      const response = await axiosInstance.get<Blob>(
        `${individualFormsUrl(
          individualId
        )}/${filledFormId}/citations/${sourceDocumentId}/pdf`,
        { responseType: "blob" }
      );
      return response.data;
    } catch (error) {
      const apiError = await handleBinaryAPIError(error);
      throw apiError;
    }
  }
};
